import { cssVar } from '@going-once/tokens';
import { Icon, type IconName } from './icons';
import { Text } from './primitives';

export type BannerTone = 'neutral' | 'danger';

/** A problem interrupts a screen reader; everything else waits for a pause. */
export function bannerRole(tone: BannerTone): 'alert' | 'status' {
  return tone === 'danger' ? 'alert' : 'status';
}

/**
 * **A note across the top of a card** (the design system's inline `Banner`): why something is unavailable
 * or read-only, in a sentence, with an icon. Not a toast: it stays until the cause is gone.
 */
export function Banner({
  message,
  tone = 'neutral',
  icon = 'circle-alert',
}: {
  message: string;
  tone?: BannerTone;
  icon?: IconName;
}) {
  const danger = tone === 'danger';
  return (
    <div
      role={bannerRole(tone)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: cssVar('space.md'),
        padding: cssVar('space.md'),
        borderRadius: cssVar('radius.md'),
        border: `1px solid ${danger ? cssVar('color.danger') : cssVar('color.border.default')}`,
        background: danger ? cssVar('color.danger.subtle') : cssVar('color.bg.sunken'),
        color: danger ? cssVar('color.danger') : cssVar('color.fg.default'),
      }}
    >
      <span style={{ flex: 'none', display: 'flex' }}>
        <Icon name={icon} />
      </span>
      <Text tone={danger ? 'danger' : 'fg.default'}>{message}</Text>
    </div>
  );
}
